import { Checkbox, ChoiceGroup, DefaultButton, IPersonaProps, Label, MessageBar, MessageBarType, NormalPeoplePicker, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Stack, TextField, Toggle } from "office-ui-fabric-react"
import { useCallback, useContext, useEffect, useState } from "react";
import { saveSite } from "../../../api/siteslocations";
import { ProfileContext } from "../../../Home";
import { IBookingConstraint } from "../../../types/Booking.types";
import { ISite } from "../../../types/SiteLocation.types"
import Utils from "../../../utils";
import { RestrictionsSection, RestrictionUpdate } from "./RestrictionsSection";

export interface ISiteFormPanelProps {
    onDismiss: (reloadSites?: boolean) => void;
    site: Partial<ISite>;
}

export const SiteFormPanel = (props: ISiteFormPanelProps) => {
    const { site } = props;

    const userContext = useContext(ProfileContext);

    const validForm = (title: string|undefined) => {
        return !(!title || title.trim() === "");
    };

    let initialSiteAdminUsers: IPersonaProps[] = [], initialDeskAccessUsers: IPersonaProps[] = [];
    if (props.site.ResourceAdminUsers && props.site.ResourceAdminTeams) {
        initialSiteAdminUsers.push(...props.site.ResourceAdminUsers.filter((sau) => sau.AccessType === 1).map((user) => ({
            text: user.FullName,
            secondaryText: user.Email,
            data: {
                userId: user.Id
            }
        } as IPersonaProps)));
        initialSiteAdminUsers.push(...props.site.ResourceAdminTeams.filter((sau) => sau.AccessType === 1).map((team) => ({
            text: team.Name,
            secondaryText: "(Team)",
            data: {
                teamId: team.Id
            }
        } as IPersonaProps)));
        initialDeskAccessUsers.push(...props.site.ResourceAdminUsers.filter((sau) => sau.AccessType === 2).map((user) => ({
            text: user.FullName,
            secondaryText: user.Email,
            data: {
                userId: user.Id
            }
        } as IPersonaProps)));
        initialDeskAccessUsers.push(...props.site.ResourceAdminTeams.filter((sau) => sau.AccessType === 2).map((team) => ({
            text: team.Name,
            secondaryText: "(Team)",
            data: {
                teamId: team.Id
            }
        } as IPersonaProps)));
    }

    // Form variables
    const [ title, setTitle ] = useState<string|undefined>(site.Title);
    const [ address, setAddress ] = useState<string|undefined>(site.Address);
    const [ description, setDescription ] = useState<string|undefined>(site.Description);
    const [ postCode, setPostCode ] = useState<string|undefined>(site.PostCode);
    const [ siteLevelParking, setSiteLevelParking ] = useState<boolean|undefined>(site.SiteLevelParking);
    const [ parkingSpaces, setParkingSpaces ] = useState<number|undefined>(site.ParkingSpaces);
    const [ notes, setNotes ] = useState<string|undefined>(site.Notes);
    const [ notificationEmail, setNotificationEmail ] = useState<string|undefined>(site.NotificationEmail);
    const [ roomBookingEnabled, setRbEnabled ] = useState<boolean|undefined>(site.RoomBookingEnabled);
    const [ deskBookingEnabled, setDbEnabled ] = useState<boolean|undefined>(site.DeskBookingEnabled);
    const [ workWeekOnly, setWorkWeekOnly ] = useState<boolean|undefined>(site.WorkWeekOnly);

    // Include teams in user searches
    const [roomIncludeTeams, setRoomIncludeTeams] = useState<boolean|undefined>(false);
    const [deskIncludeTeams, setDeskIncludeTeams] = useState<boolean|undefined>(false);

    // Booking constraints
    const [ deskConstraint, setDeskConstraint ] = useState<IBookingConstraint|undefined>(site.DeskBookingConstraint);
    const [ roomConstraint, setRoomConstraint ] = useState<IBookingConstraint|undefined>(site.RoomBookingConstraint);
    const [ enableCheckIn, setEnableCheckIn ] = useState<boolean | undefined>(site.RoomCheckIns);

    // Booking approval / access
    const [ restrictRoomAccess, setRestrictRoomAccess ] = useState<boolean|undefined>(site.RestrictRoomAccess);
    const [ restrictDeskAccess, setRestrictDeskAccess ] = useState<boolean|undefined>(site.RestrictDeskAccess);
    const [ siteAdmins, setSiteAdmins ] = useState<IPersonaProps[]|undefined>(initialSiteAdminUsers);
    const [ deskAccess, setDeskAccess ] = useState<IPersonaProps[]|undefined>(initialDeskAccessUsers);

    // Validation
    const [ error, setError ] = useState<string|undefined>();
    const [ formValid, setFormValid ] = useState(validForm(title));

    const getTitleCasedValues = (obj: {[index: string]: any}) => {
        const returnObj: {[index: string]: any} = {};
        for (let key in obj) {
            const titleCasedKey = key.charAt(0).toUpperCase() + key.substr(1);
            returnObj[titleCasedKey] = obj[key]
        }
        return returnObj;
    };

    const _saveForm = async () => {
        const formValues = getTitleCasedValues({ title, address, description, postCode, siteLevelParking, parkingSpaces, restrictDeskAccess, restrictRoomAccess, notes, notificationEmail, workWeekOnly, roomBookingEnabled, deskBookingEnabled });
        const siteAdminUserIds = siteAdmins?.filter((sa) => Boolean((sa as any).data?.userId))?.map((sa: any) => sa.data.userId);
        const roomAdminTeamIds = siteAdmins?.filter((sa) => Boolean((sa as any).data?.teamId))?.map((sa: any) => sa.data.teamId);
        const deskAccessUserIds = deskAccess?.filter((da) => Boolean((da as any).data?.userId))?.map((sa: any) => sa.data.userId);
        const deskAccessTeamIds = deskAccess?.filter((da) => Boolean((da as any).data?.teamId))?.map((sa: any) => sa.data.teamId);
        let emailSetting: string | undefined;
        if (Utils.isValidEmail(notificationEmail)) emailSetting = notificationEmail;

        const response = await saveSite({
            ...site, 
            ...formValues, 
            NotificationEmail: emailSetting,
            RoomAdminUsers: siteAdminUserIds || [],
            DeskAccessUsers: deskAccessUserIds || [],
            RoomAdminTeams: roomAdminTeamIds || [],
            DeskAccessTeams: deskAccessTeamIds || [],
            BookingConstraints: [deskConstraint, roomConstraint].filter((bc) => Boolean(bc)),
            RoomCheckIns: enableCheckIn
        } as ISite);
        if (response.ok) {
            if (userContext) {
                if (
                    (siteAdminUserIds && siteAdminUserIds.find((sa) => sa === userContext.Profile.Id)) ||
                    (deskAccessUserIds && deskAccessUserIds.find((sa) => sa === userContext.Profile.Id))
                ) {
                    const abortController = new AbortController();
                    userContext.onProfileUpdated(abortController);
                }
            }
            props.onDismiss(true);
        } else {
            setError("Unable to save site: " + response.statusText);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mapUserToPersona = useCallback(Utils.mapToPersona, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const roomUserSearch = useCallback(Utils.userSearch(roomIncludeTeams), [mapUserToPersona, roomIncludeTeams]);
    const deskUserSearch = useCallback(Utils.userSearch(deskIncludeTeams), [mapUserToPersona, deskIncludeTeams]);
    

    useEffect(() => {
        setFormValid(validForm(title));
    }, [title]);

    return (
        <Panel 
            type={PanelType.medium} 
            isOpen={true} 
            onDismiss={() => props.onDismiss()}
            headerText={site.Id === undefined ? 'New Site' : `Edit Site: ${props.site.Title || ""}`}
            isFooterAtBottom={true}
            onRenderFooter={() => <Stack horizontal horizontalAlign="end" style={{padding: 20}}>
                <PrimaryButton disabled={!formValid} iconProps={{iconName: "Save"}} text="Save" onClick={() => { _saveForm() }} style={{marginRight: 20}} />
                <DefaultButton iconProps={{iconName: "Cancel"}} text="Cancel" onClick={() => { props.onDismiss(false) }} />
            </Stack>}
        >
            <Pivot>
                <PivotItem key="siteDetails" headerText="Details">
                    { error !== undefined && (
                        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                    )}
                    <TextField autoFocus label="Site Name" value={title} onChange={(e, newValue) => setTitle(newValue)} errorMessage={!formValid ? "Title is mandatory" : undefined} />
                    <TextField multiline label="Description" value={description} onChange={(e, newValue) => setDescription(newValue)} />
                    <TextField multiline label="Address" value={address} onChange={(e, newValue) => setAddress(newValue)} />
                    <TextField label="Post Code" value={postCode} onChange={(e, newValue) => setPostCode(newValue)} />
                    { postCode !== undefined && postCode !== null && postCode.length >= 6 && (
                        <iframe title="Site Map" src={`https://maps.google.co.uk/maps?q=${encodeURIComponent(postCode)}&output=embed`} width="100%" height="300"></iframe>
                    )}
                    <Label>Site Level Parking</Label>
                    <p>If this entire site shares a single car park, enable Site Level Parking below.</p>
                    <Toggle onText="Yes" offText="No" checked={siteLevelParking} onChange={(e, checked) => setSiteLevelParking(checked)} />
                    { siteLevelParking === true && (
                        <>
                            <Label>Number of parking spaces available</Label>
                            <p>Do not include reserved spaces - ie. for execs or other companies</p>
                            <TextField type="number" value={parkingSpaces?.toString()} onChange={(e, newValue) => {
                                if (!newValue) { setParkingSpaces(0); return }
                                const parsed = parseInt(newValue, 10);
                                if (Number.isNaN(parsed)) { setParkingSpaces(0); return } 
                                setParkingSpaces(parsed);
                            }} />
                        </>
                    )}
                </PivotItem>
                <PivotItem key="notes" headerText="Features">
                    <Label>Enable / Disable Features:</Label>
                    <Toggle label="Room Booking" onText="Yes" offText="No" checked={roomBookingEnabled} onChange={(e, checked) => setRbEnabled(checked)} />
                    <Toggle label="Desk Booking" onText="Yes" offText="No" checked={deskBookingEnabled} onChange={(e, checked) => setDbEnabled(checked)} />
                    <Label>Site Notes:</Label>
                    <p>If you need to notify users of any important information regarding the site, please enter the details here. This information will be presented to users when booking a desk/room:</p>
                    <TextField autoFocus multiline label="Notes / Information" value={notes} onChange={(e, newVal) => setNotes(newVal)} />
                </PivotItem>
                <PivotItem key="restrictions" headerText="Restrictions">
                    <ChoiceGroup 
                        label="Booking Period"
                        options={[
                            { key: "workWeekOnly", text: "Work Week", iconProps: {iconName: "CalendarWorkWeek"} },
                            { key: "fullWeek", text: "Full Week", iconProps: {iconName: "CalendarWeek"} }
                        ]}
                        selectedKey={workWeekOnly ? "workWeekOnly" : "fullWeek"}
                        onChange={(ev, option) => {
                            if (option) {
                                setWorkWeekOnly(option.key === "workWeekOnly");
                            }
                        }}
                    />
                    <RestrictionsSection
                        resourceId={site.Id || -1}
                        onChange={(constraints, checkIn) => {
                            setDeskConstraint(constraints.deskBookingConstraint);
                            setRoomConstraint(constraints.roomBookingConstraint);
                            setEnableCheckIn(checkIn);
                        }}
                        updateType={RestrictionUpdate.Site}
                        deskBookingConstraint={deskConstraint}
                        roomBookingConstraint={roomConstraint}
                        roomCheckIn={enableCheckIn}
                    />
                </PivotItem>
                <PivotItem key="approval" headerText="Approval">
                    <Toggle label="Restrict room booking to Site Admins only" onText="Yes" offText="No" checked={restrictRoomAccess} onChange={(e, checked) => {
                        if (!checked && window.confirm('This will remove all room booking restrictions, allowing all users to book rooms. Restriction settings saved for individual users will be lost. Are you sure?')) {
                            setRestrictRoomAccess(checked);
                            setNotificationEmail(undefined);
                        }
                        if (checked) setRestrictRoomAccess(checked);
                    }} />
                    { restrictRoomAccess === true && (
                        <>
                            <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}}>
                                <NormalPeoplePicker
                                    styles={{root: {flexGrow: 1}}}
                                    selectedItems={siteAdmins}
                                    onResolveSuggestions={roomUserSearch}
                                    onChange={(items) => setSiteAdmins(items)}
                                    />
                                <Checkbox label="Include Teams" checked={roomIncludeTeams} onChange={(e, checked) => setRoomIncludeTeams(checked)}/>
                            </Stack>
                        </>
                    )}
                    <Toggle label="Restrict desk booking to specified users only" onText="Yes" offText="No" checked={restrictDeskAccess} onChange={(e, checked) => {
                        if (!checked && window.confirm('This will remove all desk access restrictions, allowing all users to book desks. Restriction settings saved for individual users will be lost. Are you sure?')) setRestrictDeskAccess(checked);
                        if (checked) setRestrictDeskAccess(checked);
                    }} />
                    { restrictDeskAccess === true && (
                        <>
                            <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}}>
                                <NormalPeoplePicker
                                    styles={{root: {flexGrow: 1}}}
                                    selectedItems={deskAccess}
                                    onResolveSuggestions={deskUserSearch}
                                    onChange={(items) => setDeskAccess(items)}
                                />
                                <Checkbox label="Include Teams" checked={deskIncludeTeams} onChange={(e, checked) => setDeskIncludeTeams(checked)}/>
                            </Stack>
                        </>
                    )}

                    { restrictRoomAccess === true && (
                        <>
                            { Boolean(notificationEmail) === false && (
                                <MessageBar messageBarType={MessageBarType.info}>Notifications for pending approvals can be sent to all users in the Restrict Room Booking setting above. To send to a single mailbox, enter the email address below:</MessageBar>
                            ) }
                            <TextField 
                                label="Approval Notifications" 
                                placeholder="Enter an email address" 
                                value={notificationEmail} 
                                onChange={(e, newVal) => setNotificationEmail(newVal)}
                                errorMessage={Utils.isValidEmail(notificationEmail) ? undefined : 'Please enter a valid email'}
                            />
                        </>
                    )}
                </PivotItem>
            </Pivot>
        </Panel>
    )
}